import { Link } from 'expo-router'
import { View } from 'react-native'
import { StyleSheet } from 'react-native-unistyles'

import UniButton from '@/components/unistyles/UniButton'
import UniText from '@/components/unistyles/UniText'
import useUserRoles from '@/hooks/useUserRoles'
import { useAppStore } from '@/store/useAppStore'
import buildOrgPath from '@/utils/orgRoute'
import { formatTierName, getTierLimit } from '@/utils/purchases/billing'
import trpc from '@/utils/trpc'

interface Props {
  requiresSubscription?: boolean
}

const BillingMessage = ({ requiresSubscription = true }: Props) => {
  const { isUserAdmin } = useUserRoles()
  const currentOrganization = useAppStore.use.currentOrganization().organization

  const fosterCountQuery = trpc.foster.currentMonthFosterCount.useQuery(
    {
      organizationId: currentOrganization?.id ?? '',
    },
    {
      enabled: !!currentOrganization?.id,
    }
  )

  const currentCount = fosterCountQuery.data?.count ?? 0
  const tierLimit = getTierLimit(currentOrganization?.subscriptionTier)
  const isExactlyAtLimit = currentCount === tierLimit

  if (!currentOrganization) {
    return null
  }

  // Trust the subscription check instead of comparing the count to the tier here: right after
  // an org switch the tier is unknown, and that doesn't mean the org is over its allotment
  if (!requiresSubscription || currentOrganization.subscriptionActive) {
    return null
  }

  return (
    <View
      style={
        isExactlyAtLimit ? [styles.banner, styles.warningBanner] : styles.banner
      }
      testID="deactivated-banner"
    >
      <View style={styles.content}>
        <UniText
          style={
            isExactlyAtLimit
              ? [styles.title, styles.warningTitle]
              : styles.title
          }
          variant="titleMedium"
        >
          {!isExactlyAtLimit
            ? 'Foster Quota Exceeded'
            : 'Foster Quota Limit Reached'}
        </UniText>
        {isUserAdmin ? (
          <UniText
            style={
              isExactlyAtLimit
                ? [styles.message, styles.warningMessage]
                : styles.message
            }
          >
            {!isExactlyAtLimit ? (
              <>
                Your organization has {currentCount} fosters this month,
                exceeding the
                {!currentOrganization.subscriptionTier
                  ? ` free tier limit of ${getTierLimit()} fosters`
                  : currentOrganization.subscriptionTier === 'small-rescue'
                    ? ` ${formatTierName('small-rescue')} limit of ${getTierLimit('small-rescue')} fosters`
                    : ` ${formatTierName('medium-rescue')} limit of ${getTierLimit('medium-rescue')} fosters`}
                . Please upgrade your subscription to continue managing fosters.
              </>
            ) : (
              <>
                Your organization has reached {currentCount} fosters this month,
                which is your
                {!currentOrganization.subscriptionTier
                  ? ' free tier limit'
                  : ` ${formatTierName(currentOrganization.subscriptionTier)} limit`}
                . Adding more fosters will require a subscription upgrade.
              </>
            )}
          </UniText>
        ) : (
          <UniText
            style={
              isExactlyAtLimit
                ? [styles.message, styles.warningMessage]
                : styles.message
            }
          >
            {!isExactlyAtLimit
              ? 'Your organization requires a subscription upgrade to continue. Please contact your administrator.'
              : 'Your organization has reached its foster limit. Please contact your administrator about upgrading the subscription.'}
          </UniText>
        )}

        {isUserAdmin && (
          <View style={styles.buttonContainer}>
            <Link
              asChild
              href={buildOrgPath(`/org-settings/${currentOrganization.id}/settings`)}
            >
              <UniButton
                contentStyle={styles.buttonContent}
                icon="arrow-right"
                mode="contained"
                style={
                  isExactlyAtLimit
                    ? [styles.button, styles.warningButton]
                    : styles.button
                }
              >
                View Subscription Options
              </UniButton>
            </Link>
          </View>
        )}
      </View>
    </View>
  )
}

const styles = StyleSheet.create((theme) => ({
  banner: {
    backgroundColor: theme.colors.errorContainer,
    borderRadius: theme.tokens.containerBorderRadius,
    marginHorizontal: 20,
    marginVertical: 20,
  },
  button: {
    backgroundColor: theme.colors.error,
  },
  buttonContainer: {
    marginTop: theme.tokens.spacing[4],
  },
  buttonContent: {
    flexDirection: 'row-reverse',
  },
  content: {
    padding: theme.tokens.spacing[4],
  },
  message: {
    color: theme.colors.onErrorContainer,
    fontSize: 15,
    marginTop: theme.tokens.spacing[2],
  },
  title: {
    color: theme.colors.error,
    fontWeight: '600',
  },
  warningBanner: {
    backgroundColor: theme.colors.primaryContainer,
  },
  warningButton: {
    backgroundColor: theme.colors.primary,
  },
  warningMessage: {
    color: theme.colors.onPrimaryContainer,
  },
  warningTitle: {
    color: theme.colors.primary,
  },
}))

export default BillingMessage
